import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/Auth";
import ExportData from "../../components/common/ExportData";
import { ChevronLeft, ChevronRight, Package, AlertCircle, MapPin } from "lucide-react";
import DebouncedSearchInput from "../../components/common/DebouncedSearchInput";
import SortableTableHeader from "../../components/Table/SortableTableHeader";

const ExpiringDrugsList = () => {
  const { requestWithAuth } = useAuth();
  
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  
  const [inventoryList, setInventoryList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [allInventory, setAllInventory] = useState([]);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");

  // Helper function to check if date has passed today
  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);
    return expiry < today;
  };

  // Helper function to format date and determine badge style
  const getExpiryBadgeClass = (expiryDate) => {
    if (!expiryDate) return "bg-gray-100 text-gray-700";

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      // Expired
      return "bg-red-100 text-red-800 border border-red-200";
    } else if (daysUntilExpiry <= 30) {
      // Expiring soon (within 30 days)
      return "bg-orange-100 text-orange-800 border border-orange-200";
    } else if (daysUntilExpiry <= 90) {
      // Expiring in 31-90 days
      return "bg-yellow-100 text-yellow-800 border border-yellow-200";
    } else {
      // Safe
      return "bg-green-100 text-green-800 border border-green-200";
    }
  };

  const fetchBranches = async () => {
    try {
      const data = await requestWithAuth(
        `/company/branches/?pageSize=100`,
        "GET"
      );
      setBranches(data?.results || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const fetchInventory = async (branchId) => {
    setLoading(true);
    try {
      const sortParam = sortBy
        ? `&sort_by=${sortBy}&sort_order=${sortOrder}`
        : "";
      const data = await requestWithAuth(
        `/inventory/branch/${branchId}/expiring-drugs/?paginated=false${sortParam}`,
        "GET"
      );
      setAllInventory(data);
      
      let inventoryData = [];
      for (let i = 0; i <= data?.length - 1; i++) {
        inventoryData.push({
          ID: data[i]?.drug?.id,
          Drug: data[i]?.drug?.name,
          Quantity: data[i]?.quantity,
          "Expiry Date": data[i]?.expiry_date,
          "Batch Number": data[i]?.batch_number,
        });
      }
      setTableData(inventoryData);
    } catch (error) {
      console.error("Error fetching inventory data:", error);
      setAllInventory([]);
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  const filterInventoryList = (search_query) => {
    setSearch(search_query);
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    setCurrentPage(1);
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("desc");
    }
  };

  const handleNextPage = () => {
    if (nextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (prevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3;
    const totalPagesToShow = 5;

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, idx) => (
      <button
        key={idx}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`min-w-[36px] h-[36px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${number === currentPage
            ? "bg-blue-500 text-white shadow-sm"
            : number === "..."
              ? "cursor-default text-gray-400"
              : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        {number}
      </button>
    ));
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    if (selectedBranch) {
      fetchInventory(selectedBranch);
    } else {
      setAllInventory([]);
      setTableData([]);
    }
  }, [selectedBranch, sortBy, sortOrder]);

  useEffect(() => {
    let filtered = allInventory;
    if (search) {
      filtered = allInventory.filter(
        (item) =>
          item?.drug?.toLowerCase().includes(search.toLowerCase()) ||
          item?.batch_number?.toString().includes(search.toLowerCase())
      );
    }

    const count = filtered.length;
    setTotalPages(Math.ceil(count / pageSize));

    const startIndex = (currentPage - 1) * pageSize;
    const paginated = filtered.slice(startIndex, startIndex + Number(pageSize));

    setInventoryList(paginated);
    setNextPage(currentPage < Math.ceil(count / pageSize));
    setPrevPage(currentPage > 1);
  }, [allInventory, search, currentPage, pageSize]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Inventory</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">Expiring Drugs</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Expiring Drugs</h1>
            </div>
          </div>
        </div>

        {/* Branch Selection */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <label
            htmlFor="branch-select"
            className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2"
          >
            <MapPin size={16} className="text-gray-500" />
            Select Branch to view
            <span className="text-red-500">*</span>
          </label>
          <select
            id="branch-select"
            onChange={(e) => setSelectedBranch(e.target.value)}
            value={selectedBranch}
            className="w-full max-w-md border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white transition-all"
          >
            <option value="">Choose a branch...</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name} {branch.is_head_office ? "(Head Office)" : ""}
              </option>
            ))}
          </select>
        </div>

        {/* Main Card */}
        {selectedBranch && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            {/* Filters Bar */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <ExportData tableData={tableData} fileName="Expiring Drugs" />
                  <span className="text-sm text-gray-600">Show</span>
                  <select
                    onChange={(e) => setPageSize(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    value={pageSize}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                  <span className="text-sm text-gray-600">entries</span>
                </div>

                <DebouncedSearchInput
                  onSearch={filterInventoryList}
                  placeholder="Search inventory..."
                  delay={500}
                  className="flex-1 max-w-md"
                />
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="flex items-center gap-3 text-gray-600">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                    <span>Loading inventory...</span>
                  </div>
                </div>
              ) : inventoryList?.length > 0 ? (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Item Name
                      </th>
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Branch
                      </th>
                      <SortableTableHeader
                        label="Quantity Left"
                        fieldKey="quantity"
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        label="Batch Number"
                        fieldKey="batch_number"
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        label="Expiry Date"
                        fieldKey="expiry_date"
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                      />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {inventoryList?.map((inventory, index) => (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="py-4 px-6 text-sm text-gray-900">
                          {inventory?.drug}
                        </td>
                        <td className="py-4 px-6 text-sm font-medium text-gray-900">
                          {inventory?.branch}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600">
                          {inventory?.quantity}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600">
                          {inventory?.batch_number}
                        </td>
                        <td className="py-4 px-6">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ${getExpiryBadgeClass(inventory?.expiry_date)}`}>
                            {isExpired(inventory?.expiry_date) && (
                              <AlertCircle size={12} className="flex-shrink-0" />
                            )}
                            {inventory?.expiry_date || "N/A"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="py-20 text-center">
                  <div className="text-gray-400 mb-2">
                    <Package className="mx-auto h-12 w-12" strokeWidth={1.5} />
                  </div>
                  <p className="text-gray-500 text-sm">No inventory items found</p>
                  {search && (
                    <p className="text-gray-400 text-xs mt-1">
                      Try adjusting your search terms
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Pagination */}
            {inventoryList?.length > 0 && (
              <div className="p-6 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Showing page {currentPage} of {totalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      className={`p-2 rounded-lg transition-all ${currentPage > 1
                          ? "text-gray-600 hover:bg-gray-100"
                          : "text-gray-300 cursor-not-allowed"
                        }`}
                    >
                      <ChevronLeft size={20} />
                    </button>

                    <div className="flex items-center gap-1">
                      {renderPageNumbers()}
                    </div>

                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className={`p-2 rounded-lg transition-all ${currentPage < totalPages
                          ? "text-gray-600 hover:bg-gray-100"
                          : "text-gray-300 cursor-not-allowed"
                        }`}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpiringDrugsList;
